/**
 * As cartas: bilhetes escritos hoje que só abrem no dia marcado.
 *
 * É o `HiddenNote` de `lib/features/dashboard/domain/models/hidden_note.dart`
 * com as mesmas chaves (`title`, `content`, `unlockDate`, `isRead`) e a mesma
 * ordem do `LetterboxService.notes`: por data de abertura, da mais antiga para
 * a mais nova.
 *
 * As contagens regressivas ficam aqui e não na tela porque são três telas que
 * as usam — o cartão do painel, a lista do arquivo e o aviso de carta lacrada
 * — e três cópias da mesma conta divergem na primeira correção.
 */

import type { Documento } from '../types/dados';
import { gravarData, lerData } from '../nucleo/tempo';

export interface Carta {
  id: string;
  titulo: string;
  conteudo: string;
  /** O dia e a hora em que ela deixa de estar lacrada. */
  abreEm: Date;
  lida: boolean;
}

/**
 * O `HiddenNote.fromMap`. Uma carta sem data legível abre agora: é melhor
 * mostrar do que trancar para sempre uma carta que ninguém consegue destrancar.
 */
export function cartaDeMapa(id: string, dados: Record<string, unknown>): Carta {
  return {
    id,
    titulo: typeof dados.title === 'string' ? dados.title : '',
    conteudo: typeof dados.content === 'string' ? dados.content : '',
    abreEm: lerData(dados.unlockDate) ?? new Date(),
    lida: dados.isRead === true,
  };
}

/** O `HiddenNote.toMap`, com os nomes que o Flutter gravou. */
export function cartaParaMapa(c: Omit<Carta, 'id'>): Record<string, unknown> {
  return {
    title: c.titulo,
    content: c.conteudo,
    unlockDate: gravarData(c.abreEm),
    isRead: c.lida,
  };
}

/** O `isUnlocked`: o instante marcado já passou, ou é exatamente agora. */
export function aberta(c: Carta, agora = new Date()): boolean {
  return agora.getTime() >= c.abreEm.getTime();
}

/** A caixa inteira na ordem do `LetterboxService`: por data de abertura. */
export function cartas(docs: ReadonlyArray<Documento<Record<string, unknown>>>): Carta[] {
  return docs
    .map((d) => cartaDeMapa(d.id, d.dados))
    .sort((a, b) => a.abreEm.getTime() - b.abreEm.getTime());
}

/**
 * Quanto falta, em dias inteiros e em horas inteiras.
 *
 * O Dart corta para o zero em `Duration.inDays` e `Duration.inHours`, e não
 * arredonda: faltando trinta e seis horas ele diz um dia. Aqui é `trunc` pelo
 * mesmo motivo.
 */
function falta(abreEm: Date, agora: Date): { dias: number; horas: number } {
  const ms = abreEm.getTime() - agora.getTime();
  return { dias: Math.trunc(ms / 86_400_000), horas: Math.trunc(ms / 3_600_000) };
}

/** O selo da lista: `3d`, `5h` ou `Logo`. O `_CountdownBadge` dizia `Soon`. */
export function faltaCurto(abreEm: Date, agora = new Date()): string {
  const { dias, horas } = falta(abreEm, agora);
  if (dias > 0) return `${dias}d`;
  if (horas > 0) return `${horas}h`;
  return 'Logo';
}

/** O rodapé do cartão do painel: `Abre em 3d`, `Abre em 5h` ou `Abre logo`. */
export function abreEmCurto(abreEm: Date, agora = new Date()): string {
  const { dias, horas } = falta(abreEm, agora);
  if (dias > 0) return `Abre em ${dias}d`;
  if (horas > 0) return `Abre em ${horas}h`;
  return 'Abre logo';
}

/** O aviso de carta lacrada: `3 dias`, `5 horas` ou `em breve`. */
export function faltaPorExtenso(abreEm: Date, agora = new Date()): string {
  const { dias, horas } = falta(abreEm, agora);
  if (dias > 0) return `${dias} ${dias > 1 ? 'dias' : 'dia'}`;
  if (horas > 0) return `${horas} ${horas > 1 ? 'horas' : 'hora'}`;
  return 'em breve';
}

/**
 * O `_formatDate` do arquivo: `${day}/${month}/${year}`, sem zero à esquerda.
 * Não é o `porExtenso` da casa de propósito — é a data curta que a lista de
 * cartas lacradas mostra, e ela é assim no celular.
 */
export function diaSolto(d: Date): string {
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
}
